"use client"

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import { useUnread } from '@components/messages/unread'
import { accountLinks } from '@components/session/UserNav'
import { casesLabel, usePanelLink } from '@components/session/panelLink'
import useAccountSummary from '@hooks/user/useAccountSummary'
import storage from '@utils/localStorage'
import checkLogin from '@utils/checkLogin'
import { LOGIN_PAGE } from '@constants/routes'

import styles from './SiteHeader.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST
const MENU_ID = 'konto-menu'
const iconClass = `${styles.iconBtn} ${styles.desk} ${styles.loginIcon}`
const textClass = `${styles.accountBtn} ${styles.desk} ${styles.loginText}`
const LOGIN_LABEL = 'Zaloguj się'
const ACCOUNT_LABEL = 'Moje konto'
const EDGE = 8
const GAP = 4

const accountContent = <><SvgIcon name="user" /><span>{ACCOUNT_LABEL}</span><SvgIcon name="down" size="sm" /></>

// Treść drugiego stanu niewidoczna w tej samej komórce siatki: przycisk ma tę samą szerokość przed odczytem sesji i po nim.
function Swap ({ children, other }) {
    return (
        <span className={styles.swap}>
            <span className={styles.swapOn}>{children}</span>
            <span aria-hidden="true" className={styles.swapOff}>{other}</span>
        </span>
    )
}

// Do odczytu sesji miejsce jest niewidoczne, ale zajmuje stałą szerokość (bez przesunięć po hydratacji).
export default function HeaderAccount () {
    const { data: user } = useSWR('user', storage)
    const isLoggedIn = checkLogin(user)
    const [open, setOpen] = useState(false)
    const [pos, setPos] = useState(null)
    const rootRef = useRef(null)
    const openerRef = useRef(null)
    const menuRef = useRef(null)
    const { total: unread } = useUnread()
    const summary = useAccountSummary(user, isLoggedIn && open)

    const panel = usePanelLink(isLoggedIn)

    const links = useMemo(
        () => isLoggedIn ? [...(panel ? [panel] : []), ...accountLinks(user, summary)] : [],
        [isLoggedIn, user, summary, panel],
    )

    // Lewa krawędź menu przy lewej krawędzi przycisku, 4 px pod nim; przesunięcie w lewo, gdy nie mieści się w oknie.
    useLayoutEffect(() => {
        if (!open) {
            setPos(null)
            return
        }

        const place = () => {
            const root = rootRef.current?.getBoundingClientRect()
            const btn = openerRef.current?.getBoundingClientRect()
            const width = menuRef.current?.offsetWidth || 0
            if (!root || !btn) return
            const left = Math.max(EDGE, Math.min(btn.left, document.documentElement.clientWidth - width - EDGE))
            setPos({ left: left - root.left, top: btn.bottom - root.top + GAP })
        }

        place()
        window.addEventListener('resize', place)
        return () => window.removeEventListener('resize', place)
    }, [open])

    const placed = Boolean(pos)
    useEffect(() => {
        if (placed) menuRef.current?.querySelector('a, button')?.focus()
    }, [placed])

    useEffect(() => {
        if (!open) return

        const close = () => setOpen(false)
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return
            close()
            openerRef.current?.focus()
        }
        const onOutside = (e) => {
            if (!rootRef.current?.contains(e.target)) close()
        }

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('pointerdown', onOutside)
        document.addEventListener('focusin', onOutside)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('pointerdown', onOutside)
            document.removeEventListener('focusin', onOutside)
        }
    }, [open])

    const toggle = (e) => {
        openerRef.current = e.currentTarget
        setOpen((o) => !o)
    }

    const close = () => setOpen(false)

    return (
        <div className={styles.account} data-ready={user === undefined ? undefined : ''} ref={rootRef}>
            { isLoggedIn
                ? <>
                    <button aria-controls={MENU_ID} aria-expanded={open} aria-label={ACCOUNT_LABEL} className={iconClass} onClick={toggle} type="button">
                        <SvgIcon name="user" />
                    </button>
                    <button aria-controls={MENU_ID} aria-expanded={open} className={textClass} onClick={toggle} type="button">
                        <Swap other={<span className={styles.loginWord}>{LOGIN_LABEL}</span>}>{accountContent}</Swap>
                    </button>
                    { open &&
                        <nav aria-label={ACCOUNT_LABEL} className={styles.accountMenu} id={MENU_ID} ref={menuRef}
                            style={pos || { visibility: 'hidden' }}>
                            <ul>
                                { links.map((link, i) => {
                                    const sep = i > 0 && link.group !== links[i - 1].group
                                    const pending = link.key === 'ogloszenia' && summary?.pending
                                    const messages = link.key === 'wiadomosci' && unread
                                    const cases = link.key === 'panel' && link.count
                                    const inner = <>
                                        {link.t}
                                        { cases > 0 && <span className={styles.accountBadge}>{casesLabel(cases)}</span> }
                                        { pending > 0 && <span className={styles.accountBadge}>{pending} do opłacenia</span> }
                                        { messages > 0 && <span className={styles.unreadBadge}>{messages} {messages === 1 ? 'nieprzeczytana' : 'nieprzeczytane'}</span> }
                                    </>
                                    return (
                                        <li className={sep ? styles.accountSep : undefined} key={link.key}>
                                            { link.key === 'panel'
                                                ? <a className={styles.accountLink} href={link.href}>{inner}</a>
                                                : <Link className={styles.accountLink} href={link.href} onClick={close} prefetch={false}>{inner}</Link> }
                                        </li>
                                    )
                                }) }
                            </ul>
                        </nav> }
                </>
                : <>
                    <Link aria-label={LOGIN_LABEL} className={iconClass} href={HOST + LOGIN_PAGE} prefetch={false}>
                        <SvgIcon name="user" />
                    </Link>
                    <Link className={textClass} href={HOST + LOGIN_PAGE} prefetch={false}>
                        <Swap other={accountContent}><span className={styles.loginWord}>{LOGIN_LABEL}</span></Swap>
                    </Link>
                </> }
        </div>
    )
}
